import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { LogoutModalIcon } from '@icons';

import { ConfirmationModal } from '@components/index';
import { AUTH } from '@constants/routes';
import { LOCAL_STORAGE_KEYS } from '@constants/storage';
import testId from '@constants/testId';
import {
  Event,
  MixpanelEventV2,
  sendMixpanelEvent,
  sendMixpanelEventV2
} from '@core/analytics/mixpanel';
import { useLazyLogoutQuery } from '@features/authentication/queries/AuthQuery';
import { resetAuthState } from '@features/authentication/slices/AuthSlice';
import { resetGroupState } from '@features/group-management/slices/groupSlice';
import { resetMemberState } from '@features/member-management/slices/MemberSlice';
import { exitTrialMode } from '@features/trial-mode/slices/trialSlice';
import useNotify from '@hooks/notify';
import { resetBranchState } from '@layouts/main-header/branchSlice';
import { resetUserState } from '@layouts/main-header/userSlice';
import { ApiError } from '@types';
import { clearAllLocalStorageItems, getLocalStorageItem } from '@utils/storage';

interface LogoutModalProps {
  isOpen: boolean;
  onCancelLogout: () => void;
}

const LogoutModal = ({ isOpen, onCancelLogout }: LogoutModalProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { createErrorAlert } = useNotify();

  const [logout, { isLoading: isLogoutLoading }] = useLazyLogoutQuery();

  const userId = getLocalStorageItem(LOCAL_STORAGE_KEYS.USER_ID);

  const handleLogoutConfirm = async () => {
    sendMixpanelEvent(Event.LOGOUT_CONFIRM_BUTTON_CLICKED, {
      user_id: userId
    });
    sendMixpanelEventV2(MixpanelEventV2.LOG_OUT_CONFIRMED);
    const res = await logout(userId);
    if ('data' in res) {
      dispatch(exitTrialMode());
      dispatch(resetGroupState());
      dispatch(resetUserState());
      dispatch(resetAuthState());
      dispatch(resetMemberState());
      dispatch(resetBranchState());
      clearAllLocalStorageItems();
      sendMixpanelEvent(Event.LOGOUT_SUCCESS, { user_id: userId });
      navigate(AUTH.LOGIN, { replace: true });
    } else if ('error' in res) {
      const error = res.error as ApiError;
      createErrorAlert(error.data?.message);
      sendMixpanelEvent(Event.LOGOUT_ERROR, {
        user_id: userId,
        error: error.data?.errorCode
      });
    }
  };

  return (
    <ConfirmationModal
      buttonIds={{
        confirmButton: testId.header.logoutPopup.confirm,
        cancelButton: testId.header.logoutPopup.cancel
      }}
      isOpen={isOpen}
      ModalIcon={LogoutModalIcon}
      title="Logging Out?"
      message="Are you sure you want to log out?"
      confirmButtonStyle="text-primary"
      handleConfirm={handleLogoutConfirm}
      handleCancel={onCancelLogout}
      className="w-78"
      isConfirmButtonLoading={isLogoutLoading}
    />
  );
};

export default LogoutModal;
